import type { ColumnDef, PaginationState } from "@tanstack/react-table"
import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { ChevronRight, FlaskConical, Play, Trash2 } from "lucide-react"
import { useMemo, useState } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { DataTable2 } from "@/components/ui/datatable2"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { PageHeader } from "@/components/ui/PageHeader"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { BatchesTable } from "@/features/evaluation/components/BatchesTable"
import {
  deleteBenchmarkFn,
  listBenchmarksFn,
  runBatchEvaluationFn,
  runEvaluationFn,
} from "@/features/evaluation/server/evaluationServer"
import { listConfigurationVersionsFn } from "@/features/store-configuration/server/storeConfiguration"
import { evaluationQueryKeys } from "@/lib/queryKeys"

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

const benchmarksQuery = (pagination: { pageIndex: number; pageSize: number }) =>
  queryOptions({
    queryKey: [...evaluationQueryKeys.benchmarks(), pagination],
    queryFn: () => listBenchmarksFn({ data: pagination }),
    placeholderData: keepPreviousData,
  })

const configVersionsQuery = () =>
  queryOptions({
    queryKey: ["config-versions-for-eval"],
    queryFn: () => listConfigurationVersionsFn({ data: { includeArchived: false } }),
  })

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

const DEFAULT_PAGE = { pageIndex: 0, pageSize: 20 }

export const Route = createFileRoute("/(authenticated)/settings/evaluation/")({
  loader: ({ context }) => {
    void context.queryClient.ensureQueryData(benchmarksQuery(DEFAULT_PAGE))
  },
  component: EvaluationPage,
})

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function EvaluationPage() {
  const queryClient = useQueryClient()

  const [bmPage, setBmPage] = useState<PaginationState>({ pageIndex: 0, pageSize: 20 })
  const [showRunDialog, setShowRunDialog] = useState(false)
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [showBenchmarks, setShowBenchmarks] = useState(false)

  const bmQuery = useQuery(benchmarksQuery(bmPage))
  const benchmarks = bmQuery.data?.items ?? []
  const bmTotalCount = bmQuery.data?.totalCount ?? 0

  const deleteMutation = useMutation({
    mutationFn: (benchmarkId: string) => deleteBenchmarkFn({ data: { benchmarkId } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: evaluationQueryKeys.all })
      setDeleteTarget(null)
    },
  })

  // ---- Benchmark columns ----
  const benchmarkColumns = useMemo<ColumnDef<(typeof benchmarks)[number]>[]>(
    () => [
      {
        accessorKey: "name",
        header: () => t`Name`,
        cell: ({ row }) => <span className="font-medium">{row.getValue("name")}</span>,
      },
      {
        accessorKey: "snapshotedAt",
        header: () => t`Date`,
        cell: ({ row }) => (
          <span className="text-neutral-500 text-sm">
            {new Date(row.getValue("snapshotedAt") as Date).toLocaleDateString()}
          </span>
        ),
      },
      {
        accessorKey: "projectId",
        header: () => t`Source`,
        cell: ({ row }) => (
          <span className="text-neutral-500 text-sm">
            {(row.getValue("projectId") as string | null) ? t`Local` : t`Imported`}
          </span>
        ),
      },
      {
        id: "actions",
        header: () => "",
        cell: ({ row }) => (
          <div className="flex items-center gap-1 justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs cursor-pointer"
              onClick={() => {
                setSelectedBenchmarkId(row.original.id)
                setShowRunDialog(true)
              }}
            >
              <Play className="mr-1 h-3 w-3" />
              <Trans>Run</Trans>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 cursor-pointer"
              onClick={() => setDeleteTarget({ id: row.original.id, name: row.original.name })}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <div className="flex-1 space-y-8 overflow-y-auto pb-8">
      <PageHeader
        title={t`Evaluation`}
        icon={<FlaskConical className="h-7 w-7" />}
        description={t`Compare the algorithm's automatic merchandise group placements against manually filled stores (benchmarks). A benchmark is a frozen snapshot of a store that was manually filled by a store planner — it serves as the ground truth to measure accuracy.`}
      />

      {/* 1. Benchmarks (collapsible) */}
      <section>
        <button
          onClick={() => setShowBenchmarks(!showBenchmarks)}
          className="flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-200"
        >
          <ChevronRight
            className={`h-3.5 w-3.5 transition-transform ${showBenchmarks ? "rotate-90" : ""}`}
          />
          <Trans>Benchmarks ({benchmarks.length})</Trans>
        </button>
        {showBenchmarks && (
          <div className="mt-3 rounded-lg border bg-white shadow-sm overflow-hidden dark:bg-neutral-900">
            <DataTable2
              data={benchmarks}
              columns={benchmarkColumns}
              rowCount={bmTotalCount}
              paginationState={bmPage}
              onPaginationChange={setBmPage}
              enableSorting={false}
            />
          </div>
        )}
      </section>

      {/* 3. Evaluation Batches: top-level grouping. Click a row for full detail. */}
      <div className="flex items-center justify-end -mb-2">
        {benchmarks.length > 0 && (
          <Button
            onClick={() => {
              setSelectedBenchmarkId(null)
              setShowRunDialog(true)
            }}
            size="sm"
            className="gap-1.5"
          >
            <Play className="h-3.5 w-3.5" />
            <Trans>Run evaluation</Trans>
          </Button>
        )}
      </div>
      <BatchesTable />

      {/* Dialogs */}
      <RunEvaluationDialog
        open={showRunDialog}
        onOpenChange={setShowRunDialog}
        benchmarks={benchmarks}
        preSelectedBenchmarkId={selectedBenchmarkId}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Delete Benchmark</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>
                Delete &quot;{deleteTarget?.name}&quot; and all associated runs? This cannot be
                undone.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              disabled={deleteMutation.isPending}
            >
              <Trans>Delete</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Run Evaluation Dialog
// ---------------------------------------------------------------------------

function RunEvaluationDialog({
  open,
  onOpenChange,
  benchmarks,
  preSelectedBenchmarkId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  benchmarks: Array<{ id: string; name: string }>
  preSelectedBenchmarkId: string | null
}) {
  const queryClient = useQueryClient()
  const { data: configVersions } = useQuery(configVersionsQuery())
  const [benchmarkId, setBenchmarkId] = useState("")
  const [configVersionId, setConfigVersionId] = useState("")
  const [optimizerMode, setOptimizerMode] = useState<"humanLike" | "backendPredictor">("humanLike")
  const [runAllOverride, setRunAllOverride] = useState<boolean | null>(null)
  const runAll = runAllOverride ?? !preSelectedBenchmarkId
  const setRunAll = (v: boolean) => setRunAllOverride(v)

  const effectiveBenchmarkId = preSelectedBenchmarkId ?? benchmarkId

  const runSingleMutation = useMutation({
    mutationFn: () =>
      runEvaluationFn({
        data: {
          benchmarkId: effectiveBenchmarkId,
          configVersionId,
          optimizerMode,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: evaluationQueryKeys.all })
      onOpenChange(false)
      setBenchmarkId("")
      setConfigVersionId("")
    },
  })

  const runAllMutation = useMutation({
    mutationFn: () =>
      runBatchEvaluationFn({
        data: {
          benchmarkIds: benchmarks.map((b) => b.id),
          configVersionId,
          optimizerMode,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: evaluationQueryKeys.all })
      onOpenChange(false)
      setConfigVersionId("")
    },
  })

  const isRunning = runSingleMutation.isPending || runAllMutation.isPending
  const runError = runSingleMutation.error ?? runAllMutation.error
  const versions = Array.isArray(configVersions) ? configVersions : []

  const handleRun = () => (runAll ? runAllMutation.mutate() : runSingleMutation.mutate())
  const canRun = configVersionId && (runAll ? benchmarks.length > 0 : !!effectiveBenchmarkId)

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setRunAllOverride(null)
        onOpenChange(v)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Run evaluation</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>Run the algorithm against benchmarks and compare the results.</Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!preSelectedBenchmarkId && benchmarks.length > 1 && (
            <div className="flex gap-2 rounded-lg bg-neutral-100 p-1 dark:bg-neutral-800">
              <button
                onClick={() => setRunAll(true)}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                  runAll
                    ? "bg-white text-neutral-900 shadow-sm dark:bg-neutral-700 dark:text-neutral-100"
                    : "text-neutral-500"
                }`}
              >
                <Trans>All benchmarks ({benchmarks.length})</Trans>
              </button>
              <button
                onClick={() => setRunAll(false)}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                  !runAll
                    ? "bg-white text-neutral-900 shadow-sm dark:bg-neutral-700 dark:text-neutral-100"
                    : "text-neutral-500"
                }`}
              >
                <Trans>Single benchmark</Trans>
              </button>
            </div>
          )}

          {!runAll && (
            <div className="space-y-2">
              <Label>
                <Trans>Benchmark</Trans>
              </Label>
              <Select
                value={effectiveBenchmarkId}
                onValueChange={setBenchmarkId}
                disabled={!!preSelectedBenchmarkId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t`Select benchmark`} />
                </SelectTrigger>
                <SelectContent>
                  {benchmarks.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {runAll && (
            <div className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:bg-blue-950 dark:text-blue-300">
              <Trans>
                Runs the algorithm against all {benchmarks.length} benchmark stores. This may take a
                few minutes.
              </Trans>
            </div>
          )}

          <div className="space-y-2">
            <Label>
              <Trans>Configuration version</Trans>
            </Label>
            <Select value={configVersionId} onValueChange={setConfigVersionId}>
              <SelectTrigger>
                <SelectValue placeholder={t`Select configuration`} />
              </SelectTrigger>
              <SelectContent>
                {versions.map((v: { id: string; versionLabel: string; isActive: boolean }) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.versionLabel}
                    {v.isActive ? ` (${t`active`})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>
              <Trans>Algorithm</Trans>
            </Label>
            <Select
              value={optimizerMode}
              onValueChange={(v) => setOptimizerMode(v as "humanLike" | "backendPredictor")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="humanLike">
                  {t`Legacy rule-based (TypeScript)`}
                </SelectItem>
                <SelectItem value="backendPredictor">
                  {t`Backend predictor (Fill store)`}
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-neutral-500">
              {optimizerMode === "backendPredictor" ? (
                <Trans>
                  Routes through the C# shelf-layout fill endpoint — same path as the &quot;Fill
                  store&quot; button when the e61-mg-predictor flag is on.
                </Trans>
              ) : (
                <Trans>Runs the legacy client-side rule-based optimizer.</Trans>
              )}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleRun} disabled={!canRun || isRunning}>
            {isRunning ? (
              <Trans>Running…</Trans>
            ) : runAll ? (
              <Trans>Run all ({benchmarks.length} stores)</Trans>
            ) : (
              <Trans>Run</Trans>
            )}
          </Button>
        </DialogFooter>

        {runError && (
          <p className="text-sm text-red-600">
            {runError instanceof Error ? runError.message : t`Evaluation failed`}
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
